<template>
  <div class="members-page">
    <div v-if="!loading" class="members-page__container">
      <header class="members-page__header">
        <h1 class="members-page__title">Members</h1>
        <div class="members-page__actions">
          <DealRoomInvite
            :disabled="!(canPerformAction('member:*') || canPerformAction('member:addMember'))"
            title="Add New Member"
            buttonType="addMember"
          />
          <DealRoomInvite
            :disabled="!(canPerformAction('member:*') || canPerformAction('member:addMember'))"
            title="Add Multiple Members"
            buttonType="addMultipleMembers"
          />
        </div>
      </header>

      <div class="members-page__content">
        <div class="members-page__toolbar">
          <TableNav
            :menu="tableNavMenu"
            :status="status"
            @menuClick="setStatus"
          />
          <div class="members-page__search">
            <Search
              v-if="status !== 'Waitlist'"
              :showSearchField="showSearchField"
              @search-click="showSearchField = !showSearchField"
              @search-enter="searchMembers"
              @search-input="getAllMembers"
            />
          </div>
        </div>

        <div class="members-page__table">
          <MembersTable
            v-if="members && members.length !== 0 && !searchLoading"
            :tableHeaders="tableHeaders"
            :tableRows="tableRows"
            :columnNumber="tableHeaders.length - 2"
            :columnWidth="200"
            :totalPage="pages"
            :currentPage="page"
            :deals="deals"
            :isActionAllowed="canPerformAction('member:*') || canPerformAction('member:updateMember')"
            @gotoPage="gotoPage"
            @update-members="getAllMembers({ page, status })"
          />
          <EmptyState v-if="!members || members.length === 0">
            <template v-slot:message>
              <div class="members-page__empty-message">
                <p v-if="status === 'All members' || status === ''">
                  You currently do not have anyone in your dealroom
                </p>
                <p v-if="status === 'Waitlist'">
                  There are no pending members on this account.
                </p>
                <p v-if="status === 'Inactive'">
                  There are no deactivated members on this account.
                </p>
              </div>
            </template>
          </EmptyState>
          <div v-if="searchLoading" class="members-page__loading">
            <Loader v-if="searchLoading" />
          </div>
        </div>
      </div>
    </div>
    <Loader v-if="loading" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EmptyState from "@/components/notification/EmptyState";
import TableNav from "@/layout/navigation/TableNav";
import Search from "@/components/general/Search";
import MembersTable from "@/components/tables/MembersTable";
import DealRoomInvite from "@/components/members/DealRoomInvite";

import responseHandler from "@/utils/responseHandler.js";
import { formatISOTime, formatISODate } from "@/utils/helpers.js";
import api from "@/services/org";
import filter from "@/mixins/filter";
import search from "@/mixins/search";
import { permissionsService } from "@/utils/permissionChecker.js";

export default {
  name: "Members",
  mixins: [filter, search],

  components: {
    MembersTable,
    TableNav,
    EmptyState,
    Search,
    DealRoomInvite,
  },

  data() {
    return {
      loading: false,
      btnLoading: false,
      searchLoading: false,
      showGenerateLinkModal: false,
      search: "",
      tableHeaders: ["Name", "Email", "Date Joined", "Status", "Action", "id"],
      tableRows: [],
      deals: [],
      filteredMembers: "",
      allMembers: 0,
      status: "",
      allMembers: null,
      activeMembers: null,
      deactivatedMembers: null,
      // tableNavMenu: [
      //   { title: "All members", status: "" },
      //   { title: "Active", status: "Active" },
      //   // { title: "Waitlist", status: "Waitlist" },
      //   { title: "Deactivated", status: "Inactive" },
      // ],
      filterOptions: ["Active", "Inactive"],
    };
  },

  created() {
    this.getPageInfo();
    this.getAllMembers();
  },
  computed: {
    ...mapGetters("Deals", ["dealRequests"]),
    ...mapGetters("organisationModule", ["plan"]),
    tableNavMenu() {
      return [
        {
          title: `All members (${this.allMembers?.data.data.members.length})`,
          status: "",
        },
        {
          title: `Active (${this.activeMembers?.data.data.members.length})`,
          status: "Active",
        },
        // { title: "Waitlist", status: "Waitlist" },
        {
          title: `Deactivated (${this.deactivatedMembers?.data.data.members.length})`,
          status: "Inactive",
        },
      ];
    },
    members() {
      let members = [];
      switch (this.status) {
        case "":
          members = this.allMembers?.data.data.members;
          break;
        case "Active":
          members = this.activeMembers?.data.data.members;
          break;
        case "Inactive":
          members = this.deactivatedMembers?.data.data.members;
          break;
      }
      return members;
    },
    page() {
      let page = 1;
      switch (this.status) {
        case "":
          page = this.allMembers?.data.data.page;
          break;
        case "Active":
          page = this.activeMembers?.data.data.page;
          break;
        case "value":
          page = this.deactivatedMembers?.data.data.page;
          break;
      }
      return page;
    },
    pages() {
      let pages = 1;
      switch (this.status) {
        case "value":
          pages = this.allMembers?.data.data.pages;
          break;
        case "value":
          pages = this.activeMembers?.data.data.pages;
          break;
        case "value":
          pages = this.deactivatedMembers?.data.data.pages;
          break;
      }
      return pages;
    },
  },

  methods: {
    ...mapActions(["showToast"]),
    ...mapActions("organisationModule", [
      "getOrganisationPlan",
      "getOrganisationProfile",
    ]),

    async getPageInfo() {
      const query = { page: this.page, status: this.status };
      let approvedDeals = api.getApprovedDeals();
      let allMembers = api.getAllMembers(query);
      let orgPlan = this.getOrganisationPlan();
      try {
        const response = await Promise.all([
          approvedDeals,
          allMembers,
          orgPlan,
        ]);
        const { tokens } = response[0].data.data;
        tokens.map((token) => {
          if (token.deal_access === "Public") {
            this.deals.push(token);
          }
        });
      } catch (error) {
        responseHandler.handleError(error);
        this.loading = false;
      }
    },
    setStatus(query) {
      this.status = query.status;
    },
    async getAllMembers() {
      this.loading = true;
      try {
        const [allMembers, activeMembers, deactivatedMembers] =
          await Promise.all([
            api.getAllMembers({ page: 1 }),
            api.getAllMembers({ page: 1, status: "Active" }),
            api.getAllMembers({ page: 1, status: "InActive" }),
          ]);
        this.allMembers = allMembers;
        this.activeMembers = activeMembers;
        this.deactivatedMembers = deactivatedMembers;
        this.loading = false;
      } catch (error) {
        console.log({ error });
        responseHandler.handleError(error);
        this.loading = false;
      }
    },

    async searchMembers({ value, type }) {
      if (value === "") return;
      this.searchLoading = true;
      try {
        const response = await api.searchMembers(value);
        const { data } = response.data;
        this.members = data.filter((member) => {
          if (this.status === "") return member;
          if (this.status === member.status) return member;
        });
        this.searchLoading = false;
      } catch (error) {
        this.searchLoading = false;
        responseHandler.handleError(error);
      }
    },

    resetTable({ value, type }) {
      const query = { page: this.page, status: this.status };
      if (value === "") {
        this.status = "";
      }
    },

    async gotoPage(page) {
      this.loading = true;
      const query = { page: page, status: this.status };
      try {
        await api.getAllMembers(query);
        this.loading = false;
      } catch (error) {
        responseHandler.handleError(error);
        this.loading = false;
      }
    },

    updateTableRow() {
      this.tableRows = this.members?.map((member) => {
        let name = "";
        let status = member.status;
        switch (status) {
          case "Inactive":
            status = "Deactivated";
            break;

          case "Pending_Approval":
            status = "Pending";
            break;
          default:
            break;
        }
        if (status == "Pending") {
          name = member.name;
        } else {
          name = `${member.user ? member.user.fname : member.fname} ${
            member.user ? member.user.lname : member.lname
          }`;
        }
        return {
          name: `<span class="tw-font-medium">${name}</span>`,
          email: member.user?.email ? member.user?.email : member.email,
          joined: `${formatISODate(
            member.createdAt || member.date_joined
          )}  ${formatISOTime(member.createdAt || member.date_joined)}`,
          id: `data-id/${member.member_id ? member.member_id : member._id}/${
            member.user ? member.user._id : member._id
          }`,
          status: status,
        };
      });
    },

    canPerformAction(action) {
      return permissionsService.hasPermission(action);
    },
  },
  beforeUnmount() {
    clearInterval(this.linkInterval);
    clearInterval(this.waitlistInterval);
  },
  watch: {
    members(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.getOrganisationProfile();
        this.updateTableRow();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-page: #FFFFFF;
$bg-card: #FFFFFF;
$bg-subtle: #F8FAFC;
$border-color: #E2E8F0;
$accent-primary: #4D84FF;

.members-page {
  width: 100%;
  min-height: 100vh;
  background: $bg-page;

  &__container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 32px 24px;

    @media screen and (min-width: 1024px) {
      padding: 40px 32px;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 32px;

    @media screen and (min-width: 640px) {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
    letter-spacing: -0.02em;
  }

  &__actions {
    display: flex;
    gap: 12px;
  }

  &__content {
    background: $bg-card;
    border: 1px solid $border-color;
    border-radius: 12px;
    overflow: hidden;
    margin-top: 24px;
  }

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid $border-color;
    background: $bg-subtle;
  }

  &__search {
    display: flex;
    align-items: center;
  }

  &__table {
    overflow: auto;
  }

  &__empty-message {
    text-align: center;
    color: $text-muted;
    padding: 48px 24px;
    font-size: 14px;
  }

  &__loading {
    background: $bg-card;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
